import React, { useEffect, useRef } from 'react';
import { HiX } from 'react-icons/hi';
import { ChannelUser } from '../../../types/service.types';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import UserProfileLink from '../../ui/UserDisplay/UserProfileLink';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './OrganizersModal.module.css';

interface OrganizersModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizers: ChannelUser[];
  getTranslation: (key: string) => string;
}

const OrganizersModal: React.FC<OrganizersModalProps> = ({
  isOpen,
  onClose,
  organizers,
  getTranslation
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 300,
  });

  // Get profile photo URL
  const getProfilePhoto = (organizer: ChannelUser): string | null => {
    return organizer.userAvatar || organizer.avatar || organizer.image || null;
  };

  // Get display name
  const getDisplayName = (organizer: ChannelUser): string => {
    const name = organizer.name || '';
    const lastname = organizer.lastname || '';
    return `${name} ${lastname}`.trim() || getTranslation('common.unknownUser') || 'Unknown User';
  };

  // Handle escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap for accessibility
  useEffect(() => {
    if (!isOpen || !modalContentRef.current) return;

    const modal = modalContentRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={onClose}
      onAnimationEnd={onAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="organizers-modal-title"
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''}`}
        onClick={(e) => e.stopPropagation()}
        ref={modalContentRef}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle} id="organizers-modal-title">
            {getTranslation('communities.organizers') || 'Organizers'}
          </h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={getTranslation('common.close')}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiX className={styles.closeIcon} />
          </button>
        </div>
        
        <div className={styles.organizersList}>
          {organizers.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{getTranslation('communities.noOrganizers') || 'No organizers found'}</p>
            </div>
          ) : (
            organizers.map((organizer, index) => {
              const photoUrl = getProfilePhoto(organizer);
              const displayName = getDisplayName(organizer);
              
              return (
                <UserProfileLink
                  key={organizer.userID || organizer.id || index}
                  userId={organizer.userID || organizer.id}
                  name={organizer.name}
                  lastname={organizer.lastname}
                >
                <div className={styles.organizerItem}>
                  <div className={styles.organizerAvatar}>
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={displayName}
                        className={styles.avatarImage}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector(`.${styles.avatarFallback}`);
                            if (fallback) {
                              (fallback as HTMLElement).style.display = 'flex';
                            }
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className={styles.avatarFallback}
                      style={{ 
                        display: photoUrl ? 'none' : 'flex'
                      }}
                    >
                      <ChannelInitialIcon
                        channelName={displayName}
                        size={48}
                      />
                    </div>
                  </div>
                  <div className={styles.organizerInfo}>
                    <p className={styles.organizerName}>{displayName}</p>
                  </div>
                </div>
                </UserProfileLink>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizersModal;
